import { useEffect, useState } from "react";
import { api, dateFr } from "@/lib/rentApi";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, Loading } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Wrench } from "lucide-react";
import { toast } from "sonner";

const PRIORITY = { basse: "secondary", normale: "default", haute: "destructive", urgente: "destructive" };
const STATUS_VARIANT = { ouvert: "secondary", en_cours: "default", résolu: "outline" };

export default function Tickets() {
  const { user } = useAuth();
  const role = user.role;
  const isOwner = role === "proprietaire" || role === "admin";
  const isMaintainer = role === "mainteneur";
  const canCreate = isOwner || role === "locataire";

  const [items, setItems] = useState(null);
  const [props, setProps] = useState([]);
  const [maintainers, setMaintainers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ property_id: "", title: "", description: "", priority: "normale" });

  const load = () => api.get("/tickets").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    if (canCreate) api.get("/properties").then((r) => setProps(r.data));
    if (isOwner) api.get("/users", { params: { role: "mainteneur" } }).then((r) => setMaintainers(r.data));
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tickets", form);
      toast.success("Ticket créé");
      setOpen(false); setForm({ property_id: "", title: "", description: "", priority: "normale" });
      load();
    } catch { toast.error("Erreur lors de la création"); }
  };

  const update = async (id, payload) => { await api.put(`/tickets/${id}`, payload); toast.success("Ticket mis à jour"); load(); };

  if (!items) return <Loading />;

  return (
    <div>
      <PageHeader
        title={isMaintainer ? "Interventions" : "Maintenance"}
        subtitle="Signalements et interventions techniques."
        action={canCreate && <Button onClick={() => setOpen(true)} className="rounded-full gap-2" data-testid="add-ticket-btn"><Plus className="w-4 h-4" /> Nouveau ticket</Button>}
      />

      {items.length === 0 ? (
        <EmptyState title="Aucun ticket" desc="Les demandes de maintenance apparaîtront ici." />
      ) : (
        <div className="space-y-4">
          {items.map((t) => (
            <div key={t.id} className="p-5 rounded-xl border border-border bg-card" data-testid={`ticket-${t.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Wrench className="w-4 h-4 text-muted-foreground" />
                    <h3 className="font-heading font-semibold">{t.title}</h3>
                    <Badge variant={PRIORITY[t.priority] || "default"}>{t.priority}</Badge>
                    <Badge variant={STATUS_VARIANT[t.status] || "secondary"}>{t.status}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">{t.property_title} · créé par {t.created_by_name} · {dateFr(t.created_at)}</p>
                  {t.description && <p className="text-sm mt-2 text-foreground/80">{t.description}</p>}
                  {t.assigned_name && <p className="text-xs text-muted-foreground mt-2">Affecté à : {t.assigned_name}</p>}
                </div>
                <div className="flex flex-col gap-2 items-end">
                  {isOwner && (
                    <Select value={t.assigned_to || ""} onValueChange={(v) => update(t.id, { assigned_to: v })}>
                      <SelectTrigger className="w-44" data-testid={`assign-${t.id}`}><SelectValue placeholder="Affecter…" /></SelectTrigger>
                      <SelectContent>{maintainers.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                  {(isMaintainer || isOwner) && t.status !== "résolu" && (
                    <div className="flex gap-2">
                      {t.status === "ouvert" && <Button size="sm" variant="outline" className="rounded-full" onClick={() => update(t.id, { status: "en_cours" })} data-testid={`start-${t.id}`}>Démarrer</Button>}
                      <Button size="sm" className="rounded-full" onClick={() => update(t.id, { status: "résolu" })} data-testid={`resolve-${t.id}`}>Résoudre</Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nouveau ticket</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div>
              <Label>Bien</Label>
              <Select value={form.property_id} onValueChange={(v) => setForm({ ...form, property_id: v })}>
                <SelectTrigger data-testid="ticket-property"><SelectValue placeholder="Sélectionner un bien" /></SelectTrigger>
                <SelectContent>{props.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Titre</Label><Input value={form.title} onChange={set("title")} required data-testid="ticket-title" /></div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={set("description")} rows={3} data-testid="ticket-desc" /></div>
            <div>
              <Label>Priorité</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="ticket-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="basse">Basse</SelectItem><SelectItem value="normale">Normale</SelectItem>
                  <SelectItem value="haute">Haute</SelectItem><SelectItem value="urgente">Urgente</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter><Button type="submit" className="rounded-full" data-testid="save-ticket-btn">Créer</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
